// ─────────────────────────────────────────────
// analytics/eventTaxonomy.js
// AtlasRouteEQ™ — Strict Event Taxonomy & Contract
//
// THIS IS THE SOURCE OF TRUTH for all behavioral events.
// Every page that emits events MUST use these constants.
// Every event MUST include the required fields defined here.
//
// RULE: Pages emit events. They do NOT interpret them.
// RULE: Event names are snake_case and never changed once shipped.
// RULE: All metadata fields are optional unless marked REQUIRED.
//
// NEW CODE — Commercial Use Review:
//   - No third-party libraries
//   - All logic is original IP ✅
//
// License: MIT (your original IP)
// ─────────────────────────────────────────────

// ── Event Name Registry ───────────────────────────────────────────────────────

export const EVENTS = {

  // ── App lifecycle ──────────────────────────────────────────────────────────
  APP_OPEN:               "app_open",               // User opened or resumed the app
  APP_BACKGROUND:         "app_background",         // App moved to background

  // ── Navigation ─────────────────────────────────────────────────────────────
  PAGE_VIEWED:            "page_viewed",            // Any page/tab was viewed
  PAGE_EXITED:            "page_exited",            // User navigated away from a page

  // ── Role-context separation ───────────────────────────────────────────────
  HAULER_INTEREST:         "hauler_interest",        // User explored Hauler/supply-side surfaces
  HAULER_APPLY_STARTED:    "hauler_apply_started",   // User opened or began the Hauler application
  HAULER_APPLY_COMPLETED:  "hauler_apply_completed", // User submitted the Hauler application form
  DRIVER_PORTAL_VIEWED:    "driver_portal_viewed",   // Driver/Hauler portal surface was viewed
  DRIVER_DOCS_UPLOADED:    "driver_docs_uploaded",   // Driver/Hauler document upload completed

  // ── Booking flow ───────────────────────────────────────────────────────────
  BOOKING_STARTED:        "booking_started",        // User entered the booking flow
  BOOKING_STEP_VIEWED:    "booking_step_viewed",    // User arrived at a step
  BOOKING_STEP_COMPLETED: "booking_step_completed", // User passed a step
  BOOKING_STEP_ABANDONED: "booking_step_abandoned", // User left mid-step
  BOOKING_BACKTRACKED:    "booking_backtracked",    // User went back a step
  BOOKING_CONFIRMED:      "booking_confirmed",      // Booking submitted successfully
  BOOKING_ABANDONED:      "booking_abandoned",      // User left without completing

  // ── Photo upload ───────────────────────────────────────────────────────────
  PHOTO_UPLOAD_STARTED:   "photo_upload_started",   // User tapped photo upload
  PHOTO_UPLOADED:         "photo_uploaded",         // Photo successfully uploaded
  PHOTO_UPLOAD_FAILED:    "photo_upload_failed",    // Upload error occurred

  // ── Zone & slot selection ──────────────────────────────────────────────────
  ZONE_DETECTED:          "zone_detected",          // Zone auto-detected from address
  ZONE_VIEWED:            "zone_viewed",            // User saw zone info
  SLOT_VIEWED:            "slot_viewed",            // User viewed a pickup slot
  SLOT_SELECTED:          "slot_selected",          // User selected a pickup slot
  SLOT_FULL_SEEN:         "slot_full_seen",         // User saw a fully-booked slot

  // ── Payment ────────────────────────────────────────────────────────────────
  PAYMENT_METHOD_SELECTED:"payment_method_selected",// User chose a payment type
  PAYMENT_STARTED:        "payment_started",        // User initiated payment
  PAYMENT_COMPLETED:      "payment_completed",      // Payment succeeded
  PAYMENT_FAILED:         "payment_failed",         // Payment error or cancellation
  PAYMENT_RETURNED:       "payment_returned",       // Returned from Stripe checkout

  // ── Content views ──────────────────────────────────────────────────────────
  PRICING_VIEWED:         "pricing_viewed",         // User visited the pricing page
  LEGAL_VIEWED:           "legal_viewed",           // User viewed a legal doc
  SCHEDULE_VIEWED:        "schedule_viewed",        // User viewed the schedule page
  SCHEDULE_MAP_INTERACTED:"schedule_map_interacted",// User interacted with zone map

  // ── Referral ───────────────────────────────────────────────────────────────
  REFERRAL_SHARE_INTENT:  "referral_share_intent",  // User tapped share button
  REFERRAL_LINK_COPIED:   "referral_link_copied",   // User copied link

  // ── Cancellation ──────────────────────────────────────────────────────────
  CANCELLATION_STARTED:   "cancellation_started",   // User tapped cancel request
  CANCELLATION_CONFIRMED: "cancellation_confirmed", // Cancellation submitted
  CANCELLATION_ABANDONED: "cancellation_abandoned", // User dismissed cancel flow

  // ── Reminders & nudges ─────────────────────────────────────────────────────
  REMINDER_OPENED:        "reminder_opened",        // User opened a reminder
  NUDGE_SHOWN:            "nudge_shown",            // AtlasRouteEQ showed a nudge
  NUDGE_ACTED:            "nudge_acted",            // User responded to nudge
  NUDGE_DISMISSED:        "nudge_dismissed",        // User dismissed nudge

  // ── Repeat booking ─────────────────────────────────────────────────────────
  REBOOK_TAPPED:          "rebook_tapped",          // User tapped one-tap rebook
  REBOOK_COMPLETED:       "rebook_completed",       // Rebook confirmed

  // ── AI load analysis ─────────────────────────────────────────────────
  AI_SUGGESTION_SHOWN:      "ai_suggestion_shown",      // AI analysis result displayed
  AI_SUGGESTION_APPLIED:    "ai_suggestion_applied",    // User accepted AI suggestion
  AI_SUGGESTION_OVERRIDDEN: "ai_suggestion_overridden", // User chose a different tier
  AI_ANALYSIS_FAILED:       "ai_analysis_failed",       // AI analysis returned error

  // ── Driver execution ───────────────────────────────────────────────────
  JOB_VERIFIED:             'job_verified',             // Driver submitted verified load at execution
  JOB_VERIFY_SKIPPED:       'job_verify_skipped',       // Driver marked complete without verified LEU
  MISREP_FLAGGED:           'misrep_flagged',           // Driver flagged load misrepresentation
  JOB_OPENED:               'job_opened',               // Driver opened a job detail
  TIER_SELECTED:             'tier_selected',             // Driver selected a verified tier
  FLAG_TOGGLED:              'flag_toggled',              // Driver toggled an issue flag

  // ── Waitlist + notification ───────────────────────────────────────────
  WAITLIST_JOINED:          'waitlist_joined',           // Real waitlist_entries insert succeeded
  NOTIFICATION_JOINED:      'notification_joined',       // waitlist_notifications insert succeeded
  FULL_DATE_ABANDONED:      'full_date_abandoned',       // User exited full-date panel without action

  // ── Stripe lifecycle ─────────────────────────────────────────────────
  CHECKOUT_REDIRECTED:      'checkout_redirected',       // User redirected to Stripe (pre-completion)
  STRIPE_RETURNED:          'stripe_returned',           // App detected Stripe return
  PAYMENT_CANCELED:         'payment_canceled',          // User returned from Stripe without paying

  // ── Capture capability (SHADOW MODE — observation only) ───────────────
  CAPTURE_CAPABILITY_SHADOW_DETECTED: "capture_capability_shadow_detected", // Records the environment booking opened from. No capture/UI effect.

};

// ── Required Base Fields ──────────────────────────────────────────────────────
/**
 * Every event payload MUST include these fields.
 * The eventTracker.log() function enforces this contract.
 */
export const REQUIRED_EVENT_FIELDS = [
  "event_type",   // string — one of EVENTS constants
  "user_id",      // string | null — Supabase user ID
  "session_id",   // string — from sessionTracker.getSessionId()
  "occurred_at",  // ISO string — when the event happened
];

// ── Optional Standard Metadata ────────────────────────────────────────────────
/**
 * These fields are optional but standardized.
 * Use them when relevant — do not invent new top-level field names.
 */
export const STANDARD_METADATA_FIELDS = {
  page:           "string  — which page/tab emitted this event",
  step:           "number  — booking step number (1–6)",
  zone:           "string  — zone key (A/B/C/D)",
  zone_label:     "string  — 'Blue Zone' or 'Orange Zone'",
  load_size:      "string  — Small/Half/ThreeQ/Full",
  payment_method: "string  — Card/ApplePay/Cash App",
  slot_date:      "string  — selected pickup date",
  slot_time:      "string  — selected pickup window",
  booking_id:     "string  — Supabase request ID after confirmed",
  duration_ms:    "number  — time spent (ms) on step or page",
  hesitation:     "boolean — true if duration_ms > 15000",
  amount_cents:   "number  — payment amount in cents",
  error_message:  "string  — error detail if relevant",
  intervention:   "string  — which AtlasRouteEQ intervention was active",
  outcome:        "string  — result (booked/abandoned/cancelled/etc)",
  intent:         "string  — customer_booking | hauler_interest",
  role_context:   "string  — customer | hauler",
  surface:        "string  — app surface such as resident_app or hauler_app",
};

// ── Page Event Contract ───────────────────────────────────────────────────────
/**
 * Defines what events each page is REQUIRED to emit.
 * This is the developer contract. Pages that don't emit these
 * will produce gaps in behavioral data.
 */
export const PAGE_EVENT_CONTRACT = {

  BookingFlow: {
    required: [
      EVENTS.BOOKING_STARTED,
      EVENTS.BOOKING_STEP_VIEWED,
      EVENTS.BOOKING_STEP_COMPLETED,
      EVENTS.BOOKING_CONFIRMED,
      EVENTS.BOOKING_ABANDONED,
      EVENTS.PAYMENT_STARTED,
      EVENTS.PAYMENT_COMPLETED,
    ],
    recommended: [
      EVENTS.BOOKING_BACKTRACKED,
      EVENTS.BOOKING_STEP_ABANDONED,
      EVENTS.PHOTO_UPLOAD_STARTED,
      EVENTS.PHOTO_UPLOADED,
      EVENTS.PHOTO_UPLOAD_FAILED,
      EVENTS.ZONE_DETECTED,
      EVENTS.SLOT_VIEWED,
      EVENTS.SLOT_SELECTED,
      EVENTS.SLOT_FULL_SEEN,
      EVENTS.PAYMENT_METHOD_SELECTED,
      EVENTS.PAYMENT_FAILED,
      EVENTS.AI_SUGGESTION_SHOWN,
      EVENTS.AI_SUGGESTION_APPLIED,
      EVENTS.AI_SUGGESTION_OVERRIDDEN,
      EVENTS.AI_ANALYSIS_FAILED,
    ],
  },

  PricingPage: {
    required: [EVENTS.PRICING_VIEWED],
    recommended: [],
  },

  HaulerApplyPage: {
    required: [EVENTS.HAULER_APPLY_STARTED, EVENTS.HAULER_APPLY_COMPLETED],
    recommended: [EVENTS.HAULER_INTEREST],
  },

  HaulerApp: {
    required: [EVENTS.DRIVER_PORTAL_VIEWED],
    recommended: [EVENTS.HAULER_INTEREST, EVENTS.DRIVER_DOCS_UPLOADED],
  },

  SchedulePage: {
    required: [EVENTS.SCHEDULE_VIEWED],
    recommended: [EVENTS.SCHEDULE_MAP_INTERACTED, EVENTS.ZONE_VIEWED],
  },

  ReferralPage: {
    required: [],
    recommended: [EVENTS.REFERRAL_SHARE_INTENT, EVENTS.REFERRAL_LINK_COPIED],
  },

  LegalScreen: {
    required: [],
    recommended: [EVENTS.LEGAL_VIEWED],
  },

  RequestsPage: {
    required: [],
    recommended: [EVENTS.CANCELLATION_STARTED, EVENTS.CANCELLATION_CONFIRMED, EVENTS.CANCELLATION_ABANDONED],
  },

};

// ── Decision Output Contract ──────────────────────────────────────────────────
/**
 * The shape of what AtlasRouteEQ's decision engine is ALLOWED to return.
 * Pages render decisions — they do not invent their own.
 *
 * @typedef {object} DecisionOutput
 * @property {string}  state               - Behavioral state label
 * @property {boolean} showMessage         - Whether to show a message
 * @property {string|null} messageType     - "reassurance" | "urgency" | "simplify" | "rebook" | null
 * @property {string|null} messageHeadline - Short headline copy
 * @property {string|null} messageBody     - Body copy
 * @property {string|null} ctaLabel        - Override for CTA button label
 * @property {string|null} recommendedAction - "show_best_slot" | "prefill_values" | "skip_steps" | null
 * @property {number[]}    skipSteps       - Step numbers to skip (e.g. [2])
 * @property {boolean}     suppressNudges  - Stop showing further nudges
 * @property {string|null} intervention    - Which intervention was selected
 */
export const DECISION_OUTPUT_SCHEMA = {
  state:             "string  — urgency_uncertain | high_friction | low_trust | habitual | standard",
  showMessage:       "boolean — whether to show an AtlasRouteEQ message",
  messageType:       "string  — reassurance | urgency | simplify | rebook | route_fill | null",
  messageHeadline:   "string  — short headline copy for message",
  messageBody:       "string  — body copy for message",
  ctaLabel:          "string  — override for the primary CTA button",
  recommendedAction: "string  — show_best_slot | prefill_values | skip_steps | null",
  skipSteps:         "number[] — booking step numbers to skip",
  suppressNudges:    "boolean — if true, stop showing further interventions",
  intervention:      "string  — which INTERVENTIONS key was used",
};

// ── Behavioral State Labels ───────────────────────────────────────────────────
export const BEHAVIORAL_STATES = {
  URGENT_UNCERTAIN: "urgent_uncertain",   // High urgency + low trust
  HIGH_FRICTION:    "high_friction",      // Repeated hesitation / abandonment
  LOW_TRUST:        "low_trust",          // Pricing/legal views + no booking
  HABITUAL:         "habitual",           // Repeat confirmed bookings
  ROUTE_FILL:       "route_fill",         // Zone has open capacity to fill
  STANDARD:         "standard",          // No strong signal detected
};
